// -----------------------------------------------------------------------------
// Sidebar — Menu de navigation latéral + user badge + sync status + logout
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

import { T } from "../i18n/translations";
import { getColors } from "../styles/theme";
import { Ic, ICONS } from "./Icons";

export default function Sidebar({
  // Données
  lang,
  dark,
  accent,
  authUser,
  userProfile,
  isStaff,
  totalAlerts,
  syncStatus,
  online,
  // UI state
  view,
  setView,
  sidebarOpen,
  setSidebarOpen,
  isMobile,
  // Actions
  onLogout,
  onExportList,
}) {
  const t = T[lang];
  const { textPrimary, textSecondary, borderColor } = getColors(dark);

  const navItems = [
    { id: "dashboard", icon: ICONS.grid,    label: t.dashboard },
    { id: "products",  icon: ICONS.box,     label: t.products },
    { id: "alerts",    icon: ICONS.bell,    label: t.alerts, badge: totalAlerts },
    { id: "history",   icon: ICONS.history, label: t.history },
    { id: "inventory", icon: ICONS.clip,    label: t.inventory },
    ...(!isStaff
      ? [
          { id: "licence",  icon: ICONS.key,     label: t.licence },
          { id: "contact",  icon: ICONS.headset, label: t.contact },
          { id: "settings", icon: ICONS.sun,     label: t.settings },
        ]
      : [{ id: "contact", icon: ICONS.headset, label: t.contact }]),
  ];

  return (
    <>
      {/* Overlay mobile */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:40 }}
        />
      )}

      {/* Bouton burger mobile */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ position:"fixed", top:12, left:12, zIndex:60, width:38, height:38, borderRadius:10, background:dark?"rgba(255,255,255,.1)":"rgba(0,0,0,.08)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:textPrimary }}
        >
          ☰
        </button>
      )}

      {/* Sidebar */}
      <div style={{ width:205, background:dark?"rgba(255,255,255,.02)":"rgba(255,255,255,.85)", borderRight:`1px solid ${borderColor}`, display:"flex", flexDirection:"column", padding:"18px 12px", flexShrink:0, ...(isMobile?{position:"fixed",top:0,left:sidebarOpen?0:-220,height:"100vh",zIndex:50,transition:"left .25s ease",boxShadow:sidebarOpen?"4px 0 20px rgba(0,0,0,.4)":"none",background:dark?"#0d0d1a":"#ffffff"}:{}) }}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:20 }}>
          <div style={{ width:32, height:32, background:`linear-gradient(135deg,${accent},#f7931e)`, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <Ic d={ICONS.box} size={16} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:textPrimary }}>{t.appName}</div>
            <div style={{ fontSize:9, color:textSecondary, letterSpacing:"1.5px" }}>VISUALISPRO</div>
          </div>
        </div>

        {/* User badge */}
        <div style={{ background:`${accent}15`, border:`1px solid ${accent}30`, borderRadius:9, padding:"8px 10px", marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:accent, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {userProfile?.name || authUser.email}
          </div>
          <div style={{ fontSize:10, color:textSecondary, marginTop:1 }}>{userProfile?.type || "Restaurant"}</div>
        </div>

        {/* Sync status */}
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 9px", borderRadius:8, marginBottom:12, background:syncStatus==="saving"?"rgba(245,158,11,.08)":syncStatus==="error"?"rgba(239,68,68,.08)":"rgba(16,185,129,.08)", border:`1px solid ${syncStatus==="saving"?"rgba(245,158,11,.2)":syncStatus==="error"?"rgba(239,68,68,.2)":"rgba(16,185,129,.2)"}` }}>
          {syncStatus === "saving"
            ? <span style={{ display:"inline-block", width:10, height:10, border:"2px solid rgba(245,158,11,.3)", borderTopColor:"#f59e0b", borderRadius:"50%", animation:"spin .8s linear infinite" }} />
            : <Ic d={syncStatus==="error"?ICONS.warn:ICONS.cloud} size={11} color={syncStatus==="error"?"#f87171":"#10b981"} />}
          <span style={{ fontSize:10, fontWeight:600, color:syncStatus==="saving"?"#f59e0b":syncStatus==="error"?"#f87171":"#10b981" }}>
            {syncStatus==="saving"?t.saving:syncStatus==="error"?t.syncError:t.synced}
          </span>
        </div>

        {/* Bannière offline */}
        {!online && (
          <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:999, background:"rgba(245,158,11,.95)", padding:"8px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, backdropFilter:"blur(10px)" }}>
            <Ic d={ICONS.wifi} size={14} color="#000" />
            <span style={{ fontSize:13, color:"#000", fontWeight:700 }}>⚡ Mode hors-ligne actif. Synchronisation en attente.</span>
          </div>
        )}

        {/* Nav items */}
        <nav style={{ display:"flex", flexDirection:"column", gap:2 }}>
          {navItems.map((n) => (
            <button
              key={n.id}
              className={`nav-btn${view===n.id?" active":""}`}
              onClick={() => { setView(n.id); if (isMobile) setSidebarOpen(false); }}
              style={{ color:view===n.id?accent:undefined, background:view===n.id?`${accent}12`:undefined }}
            >
              <Ic d={n.icon} size={15} color={view===n.id?accent:textSecondary} />
              <span style={{ fontSize:12 }}>{n.label}</span>
              {n.badge > 0 && (
                <span style={{ marginLeft:"auto", minWidth:17, height:17, background:"#ef4444", borderRadius:"50%", fontSize:10, fontWeight:700, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>
                  {n.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div style={{ flex: 1 }} />

        {/* Export liste courses */}
        <button
          onClick={onExportList}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 10px", borderRadius:9, fontSize:12, fontWeight:600, color:"#10b981", background:"rgba(16,185,129,.08)", border:"1px solid rgba(16,185,129,.15)", cursor:"pointer", marginBottom:7, width:"100%" }}
        >
          <Ic d={ICONS.dl} size={14} color="#10b981" /> {t.exportList}
        </button>

        {/* Logout */}
        <button className="nav-btn" onClick={onLogout} style={{ color:"#ef4444" }}>
          <Ic d={ICONS.logout} size={14} color="#ef4444" />
          <span style={{ fontSize:12 }}>{t.logout}</span>
        </button>
      </div>
    </>
  );
}
